import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { canonicalUrl } from "@/config/canonical";
import { GAME_NAME } from "@/config/game";
import { HeroSection } from "@/containers/HeroSection";
import { Button } from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import { CompletedChip } from "@/design-system/CompletedChip/CompletedChip";
import { Foldout } from "@/design-system/Foldout/Foldout";
import { IconButton } from "@/design-system/IconButton/IconButton";
import { Skeleton } from "@/design-system/Skeleton/Skeleton";
import { CoinIcon } from "@/designSystemIcons";
import { useStartClubImport } from "@/features/MyClub/EmptyClub/useStartClubImport";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { EA_WEB_APP_URL } from "@/settings";
import { safeGetJSON, safeSetItem } from "@/utils/safeStorage";
import type { GalleryGradeName } from "@repo/shared/api";
import {
  GALLERY_STORAGE_KEYS,
  compact,
  gradeHintKey,
  sortGalleryItems,
  tokensUpTo,
} from "@repo/shared/gallery";
import { useGallerySet } from "@repo/shared/hooks";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { ArrowRight, Check, Copy } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useParams, useSearchParams } from "react-router";

import { GalleryBadge } from "./components/GalleryBadge";
import { StepNumber } from "./components/GalleryFirstOwnerSheet";
import {
  CompletedGradePicker,
  GalleryTokenIcon,
  GradeDiamond,
} from "./components/GallerySetCard";
import { GallerySolutionView } from "./components/GallerySolutionView";
import { GalleryStatTip, statLabel } from "./components/GalleryStatTip";

// The solve guide starts closed and pulses until first toggled; after that it remembers its state.
const CLUB_IMPORT_ORIGIN = { section: "Gallery", subsection: "Set" };

export const GallerySetPage = () => {
  const { t } = useTranslation("gallery");
  const { categorySlug = "", setSlug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const goBack = useGoBack({ fallback: "/fut-gallery" });
  const { user } = useSelector(getUserSelector);
  const dispatch = useDispatch<AppDispatch>();
  const { pathname } = useLocation();
  const startClubImport = useStartClubImport(CLUB_IMPORT_ORIGIN);
  // Like the gallery page: playerCount, since the profile loads before any club fetch.
  const clubImported = !!user.uuid && user.playerCount > 0;
  // Null until the guide is first toggled.
  const [guideOpen, setGuideOpen] = useState(() =>
    safeGetJSON<boolean>(GALLERY_STORAGE_KEYS.solveGuideOpen),
  );
  const {
    set,
    status,
    solution,
    buildStatus,
    refreshing,
    solutionBuilt,
    buildSolution,
    completedIndex,
    changeCompletedGrade,
    justCompleted,
    dismissJustCompleted,
    grades,
    selectedIndex,
    grade,
    pickGrade,
    collectedCount,
    marks,
    toggleCollected,
    toggleFirstOwner,
  } = useGallerySet({
    categorySlug,
    setSlug,
    userUuid: user.uuid,
    // The card's See Solution opens on the grade selected there.
    initialGrade: (searchParams.get("grade") ?? undefined) as
      | GalleryGradeName
      | undefined,
  });
  const name = set?.name ?? "";

  return (
    <main className="z-10 h-full min-h-screen flex-1 bg-background text-white">
      <HeroSection
        helmetTitle={t("seo.setTitle", { name })}
        helmetDescription={t("seo.setDescription", { name, game: GAME_NAME })}
        pageTitle={t("seo.setPageTitle", { name, game: GAME_NAME })}
        helmetCanonicalLink={canonicalUrl(
          `/fut-gallery/${categorySlug}/${setSlug}`,
        )}
        back={goBack}
        shouldBeInTheDocumentFlow
        hidePageDescription
      />
      {status === "rejected" ? (
        <p className="mt-6 text-center text-sm text-foreground-muted">
          {t("set.error")}
        </p>
      ) : (
        <div className="mx-auto flex w-full max-w-content flex-col gap-y-4 px-4 pb-12 pt-6 large-desktop:px-8">
          <Card hoverable={false} className="flex flex-col gap-y-4 p-4">
            <header className="flex items-center gap-x-4">
              <GalleryBadge badge={set?.badge ?? null} className="size-16" />
              <div className="min-w-0 flex-1">
                <h2 className="text-xl font-semibold">
                  {set ? set.name : <Skeleton className="w-40" />}
                </h2>
                <p className="text-sm text-foreground-muted">
                  {set ? set.description : <Skeleton className="w-64" />}
                </p>
                {set && !set.active && (
                  <p className="text-sm text-foreground-muted">
                    {t("set.inactive")}
                  </p>
                )}
              </div>
              {set?.active && (
                <span className="flex items-center gap-x-1.5 self-start">
                  {/* Signed out it asks to log in, since completion is saved on the account. */}
                  {user.uuid ? (
                    <CompletedGradePicker
                      grades={set.grades}
                      completedIndex={completedIndex}
                      selectedIndex={selectedIndex}
                      onChange={changeCompletedGrade}
                    />
                  ) : (
                    <IconButton
                      aria-label={t("completion.title")}
                      icon={<Check className="size-4 text-foreground-muted" />}
                      onClick={(e) => {
                        // The modal's outside-click listener would catch this same click.
                        e.stopPropagation();
                        dispatch(showModal({ modalType: "login-required" }));
                      }}
                    />
                  )}
                </span>
              )}
            </header>

            {set?.active &&
              solution &&
              grade &&
              selectedIndex !== undefined && (
                <>
                  <div className="flex flex-col gap-4 touch-device:flex-row touch-device:items-center touch-device:gap-6">
                    <div className="flex items-center justify-between gap-x-4">
                      <ol className="flex items-center -space-x-[3px]">
                        {set.grades.map((g, i) => {
                          const reachable = !!grades[i]?.reachable;
                          const completed = i <= completedIndex;
                          const diamond = (
                            <GradeDiamond
                              grade={g.name}
                              selected={i === selectedIndex}
                              reachable={reachable}
                              completed={completed}
                              onSelect={() => pickGrade(g.name)}
                            />
                          );
                          const hintKey = gradeHintKey(
                            grades[i] ?? { reachable, onMarket: true },
                            completed,
                          );
                          const hint = hintKey && t(hintKey);
                          return (
                            <li key={g.name} className="flex items-center">
                              {hint ? (
                                <Tooltip content={hint}>{diamond}</Tooltip>
                              ) : (
                                diamond
                              )}
                            </li>
                          );
                        })}
                      </ol>
                      {/* Tokens the selected grade earns (every grade up to it), out of the set total. */}
                      <span className="flex shrink-0 items-center gap-1.5 text-lg font-bold tabular-nums">
                        <GalleryTokenIcon className="size-6" />
                        <span>{tokensUpTo(set.grades, selectedIndex)}</span>
                        <span className="text-sm font-normal text-foreground-muted">
                          / {set.totalTokens}
                        </span>
                      </span>
                    </div>
                    <div
                      className={cn(
                        "grid grid-cols-3 gap-2 touch-device:w-[420px]",
                        refreshing && "animate-pulse",
                      )}
                    >
                      <GalleryStatTip
                        icon={
                          <>
                            <span className="flex items-center gap-1 text-sm font-semibold tabular-nums">
                              <CoinIcon size={14} />
                              <span>{compact.format(grade.budgetNeeded)}</span>
                            </span>
                            <span className={statLabel}>
                              {t("list.budget")}
                            </span>
                          </>
                        }
                      >
                        <span className="whitespace-pre-line">
                          {t("list.budgetInfo")}
                        </span>
                      </GalleryStatTip>
                      <GalleryStatTip
                        icon={
                          <>
                            <span className="flex items-center gap-1 text-sm font-semibold tabular-nums">
                              <CoinIcon size={14} />
                              <span>{compact.format(grade.taxLoss)}</span>
                            </span>
                            <span className={statLabel}>
                              {t("solution.taxLoss")}
                            </span>
                          </>
                        }
                      >
                        {t("list.taxLossInfo")}
                      </GalleryStatTip>
                      <GalleryStatTip
                        icon={
                          <>
                            <span className="flex items-center gap-1 text-sm font-semibold tabular-nums">
                              <span>
                                {collectedCount} / {grade.items.length}
                              </span>
                              {/* Every card of the grade is collected: nothing to buy. */}
                              {grade.items.length > 0 &&
                                collectedCount === grade.items.length && (
                                  <Check className="size-3.5 text-streamline-success" />
                                )}
                            </span>
                            <span className={statLabel}>
                              {t("list.collectedPlayers")}
                            </span>
                          </>
                        }
                      >
                        {t("list.collectedInfo")}
                      </GalleryStatTip>
                    </div>
                  </div>
                  {!grade.reachable ? (
                    <p className="text-sm text-foreground-muted">
                      {t("solution.notReachable")}
                    </p>
                  ) : (
                    !grade.onMarket && (
                      <p className="text-sm text-amber-400">
                        {t("solution.notOnMarket")}
                      </p>
                    )
                  )}
                  <Foldout
                    label={
                      <span
                        className={cn(guideOpen === null && "animate-pulse")}
                      >
                        {t("guide.title")}
                      </span>
                    }
                    open={!!guideOpen}
                    onToggle={() => {
                      setGuideOpen(!guideOpen);
                      safeSetItem(
                        GALLERY_STORAGE_KEYS.solveGuideOpen,
                        JSON.stringify(!guideOpen),
                      );
                    }}
                  >
                    <ol className="flex flex-col gap-y-3 text-sm">
                      <li className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <StepNumber n={1} />
                        {clubImported ? (
                          <CompletedChip className="shrink-0">
                            {t("club.imported")}
                          </CompletedChip>
                        ) : (
                          <Button
                            variant="tertiary"
                            size="small"
                            className="shrink-0"
                            onClick={() =>
                              void startClubImport({ returnTo: pathname })
                            }
                          >
                            {t("club.import")}
                          </Button>
                        )}
                        <ArrowRight className="size-4 shrink-0 text-foreground-muted" />
                        {solutionBuilt ? (
                          <CompletedChip className="shrink-0">
                            {t("guide.solutionBuilt")}
                          </CompletedChip>
                        ) : (
                          <Button
                            variant="tertiary"
                            size="small"
                            className="shrink-0"
                            disabled={refreshing || !clubImported}
                            onClick={() => void buildSolution()}
                          >
                            {t(
                              refreshing
                                ? "guide.building"
                                : "guide.buildSolution",
                            )}
                          </Button>
                        )}
                        {!clubImported && (
                          <>
                            <span aria-hidden className="text-foreground-muted">
                              &bull;
                            </span>
                            <span className="text-foreground-muted">
                              {t("guide.importPitch")}
                            </span>
                          </>
                        )}
                        {buildStatus === "rejected" && (
                          <span className="text-red-400">
                            {t("solution.error")}
                          </span>
                        )}
                      </li>
                      <li className="flex items-start gap-x-2">
                        <StepNumber n={2} />
                        <div className="flex flex-col gap-y-1">
                          <span>{t("guide.step2")}</span>
                          <ul className="flex flex-col gap-y-0.5 text-foreground-muted">
                            <li>
                              <Trans
                                ns="gallery"
                                i18nKey="guide.step2a"
                                components={{
                                  icon: (
                                    <Copy className="inline size-3.5 align-[-2px]" />
                                  ),
                                }}
                              />
                            </li>
                            <li>
                              <Trans
                                ns="gallery"
                                i18nKey="guide.step2b"
                                components={{
                                  app: (
                                    <a
                                      href={EA_WEB_APP_URL}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="text-blue-400 underline"
                                    />
                                  ),
                                }}
                              />
                            </li>
                            <li>{t("guide.step2c")}</li>
                            <li>{t("guide.step2d")}</li>
                          </ul>
                        </div>
                      </li>
                      <li className="flex items-start gap-x-2">
                        <StepNumber n={3} />
                        <div className="flex flex-col gap-y-1">
                          <span>{t("guide.step3")}</span>
                          <span className="text-foreground-muted">
                            <Trans
                              ns="gallery"
                              i18nKey="guide.step3Hint"
                              components={{
                                contact: (
                                  <NavLink
                                    to="/contact"
                                    className="text-blue-400 underline"
                                  />
                                ),
                              }}
                            />
                          </span>
                        </div>
                      </li>
                      <li className="flex items-start gap-x-2">
                        <StepNumber n={4} />
                        <span>{t("guide.step4")}</span>
                      </li>
                    </ol>
                  </Foldout>
                </>
              )}
          </Card>

          {set?.active && grade?.reachable && (
            <GallerySolutionView
              items={sortGalleryItems(grade.items)}
              collectedMarks={marks.collected}
              firstOwnerMarks={marks.firstOwner}
              onToggleCollected={toggleCollected}
              onToggleFirstOwner={toggleFirstOwner}
            />
          )}
        </div>
      )}
      {justCompleted && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-black/60 px-4 py-8 backdrop-blur-[2px] transition-opacity duration-200 starting:opacity-0"
          onClick={dismissJustCompleted}
        >
          <div
            className="mx-auto flex w-full max-w-[420px] flex-col items-center gap-4 rounded-2xl border border-streamline-current-border bg-streamline-current-surface px-6 py-8 text-center transition-[opacity,transform] duration-200 ease-out starting:scale-95 starting:opacity-0"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-2xl font-extrabold text-white">
              {t("completion.doneTitle", { name })}
            </span>
            {/* The grade's diamond, scaled up, beside the tokens it earned, split like the streamlined panel's stats. */}
            <div className="flex items-center gap-6">
              <span className="[&>span]:size-14">
                <GradeDiamond
                  grade={justCompleted.grade}
                  selected={false}
                  reachable
                  completed={false}
                />
              </span>
              {justCompleted.tokens > 0 && (
                <>
                  <div className="h-[38px] w-px bg-streamline-current-border" />
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-neutral-500">
                      {t("completion.earned")}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <GalleryTokenIcon className="size-5" />
                      <span className="text-xl font-extrabold tabular-nums text-white">
                        +{justCompleted.tokens}
                      </span>
                    </span>
                  </div>
                </>
              )}
            </div>
            <Button
              variant="primary"
              size="medium"
              onClick={dismissJustCompleted}
            >
              {t("common:close")}
            </Button>
          </div>
        </div>
      )}
    </main>
  );
};

export default GallerySetPage;
